import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './PhoneVerification.css';
import { supabase } from '../../lib/supabase';
import iconBack from '../../assets/images/icon-back.svg';

// Kakao SDK 타입 선언
declare global {
  interface Window {
    Kakao: any;
  }
}

interface LocationState {
  snsType?: 'kakao' | 'naver' | 'apple';
  snsId?: string;
  email?: string;
  name?: string;
}

const PhoneVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  /**
   * SMS 인증 기능 활성화 여부
   *
   * true: SMS 인증번호 발송 및 검증 활성화 (Coolsms 설정 필요)
   * false: 휴대폰 번호만 입력받고 바로 다음 단계로 이동
   *
   * SMS 인증 활성화 방법:
   * 1. SMS_SETUP.md 파일 참고
   * 2. Coolsms 가입 및 API 키 발급
   * 3. Supabase Edge Functions 배포
   * 4. 이 값을 true로 변경
   */
  const ENABLE_SMS_VERIFICATION = false;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [timer, setTimer] = useState(180); // 3분
  const [isVerified, setIsVerified] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [codeError, setCodeError] = useState('');

  // SNS 로그인 데이터 확인
  useEffect(() => {
    if (!state?.snsType || !state?.snsId) {
      alert('잘못된 접근입니다.');
      navigate('/login');
    }
  }, [state, navigate]);

  // 타이머
  useEffect(() => {
    if (isCodeSent && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isCodeSent, timer]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const numbers = phone.replace(/[^\d]/g, '');
    // 한국 휴대폰 번호: 010으로 시작, 총 11자리
    const phoneRegex = /^010\d{8}$/;
    return phoneRegex.test(numbers);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setPhoneError(''); // 입력 시 에러 메시지 제거
  };

  const handleSendCode = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('올바른 휴대폰 번호를 입력해주세요.');
      return;
    }

    try {
      // Supabase Edge Function 호출
      const { data, error } = await supabase.functions.invoke('sms-send', {
        body: { phone: phoneNumber.replace(/[^\d]/g, '') }
      });

      if (error) {
        console.error('[SMS] 발송 오류:', error);
        setPhoneError('인증번호 발송에 실패했습니다.');
        return;
      }

      if (!data.success) {
        setPhoneError(data.error || '인증번호 발송에 실패했습니다.');
        return;
      }

      console.log('[SMS] 발송 성공:', data.message);

      // 개발 환경에서는 인증번호를 콘솔에 표시
      if (data.code) {
        console.log('[SMS] 인증번호 (개발용):', data.code);
        alert(`인증번호가 발송되었습니다.\n개발용: ${data.code}`);
      } else {
        alert('인증번호가 발송되었습니다.');
      }

      setIsCodeSent(true);
      setTimer(180);
      setPhoneError('');
    } catch (error) {
      console.error('[SMS] 발송 오류:', error);
      setPhoneError('인증번호 발송 중 오류가 발생했습니다.');
    }
  };

  const handleVerifyCode = async () => {
    // 타이머 만료 체크
    if (timer === 0) {
      setCodeError('인증번호가 만료되었습니다.');
      return;
    }

    if (verificationCode.length !== 6) {
      setCodeError('인증번호 6자리를 입력해주세요.');
      return;
    }

    try {
      // Supabase Edge Function 호출
      const { data, error } = await supabase.functions.invoke('sms-verify', {
        body: {
          phone: phoneNumber.replace(/[^\d]/g, ''),
          code: verificationCode
        }
      });

      if (error) {
        console.error('[SMS] 검증 오류:', error);
        setCodeError('인증번호 확인에 실패했습니다.');
        return;
      }

      if (!data.success) {
        setCodeError(data.error || '인증번호가 일치하지 않습니다.');
        return;
      }

      console.log('[SMS] 인증 성공:', data.message);
      setIsVerified(true);
      setCodeError('');
      alert('인증이 완료되었습니다.');
    } catch (error) {
      console.error('[SMS] 검증 오류:', error);
      setCodeError('인증번호 확인 중 오류가 발생했습니다.');
    }
  };

  const handleResendCode = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('올바른 휴대폰 번호를 입력해주세요.');
      return;
    }

    try {
      // Supabase Edge Function 호출
      const { data, error } = await supabase.functions.invoke('sms-send', {
        body: { phone: phoneNumber.replace(/[^\d]/g, '') }
      });

      if (error) {
        console.error('[SMS] 재발송 오류:', error);
        setCodeError('인증번호 재발송에 실패했습니다.');
        return;
      }

      if (!data.success) {
        setCodeError(data.error || '인증번호 재발송에 실패했습니다.');
        return;
      }

      console.log('[SMS] 재발송 성공:', data.message);

      // 개발 환경에서는 인증번호를 콘솔에 표시
      if (data.code) {
        console.log('[SMS] 인증번호 (개발용):', data.code);
        alert(`인증번호가 재발송되었습니다.\n개발용: ${data.code}`);
      } else {
        alert('인증번호가 재발송되었습니다.');
      }

      setTimer(180);
      setCodeError('');
      setVerificationCode('');
    } catch (error) {
      console.error('[SMS] 재발송 오류:', error);
      setCodeError('인증번호 재발송 중 오류가 발생했습니다.');
    }
  };

  const handleComplete = () => {
    // SMS 인증이 활성화된 경우에만 인증 확인
    if (ENABLE_SMS_VERIFICATION && !isVerified) {
      alert('휴대폰 인증을 완료해주세요.');
      return;
    }

    // SMS 인증이 비활성화된 경우 번호만 검증
    if (!ENABLE_SMS_VERIFICATION) {
      if (!validatePhoneNumber(phoneNumber)) {
        setPhoneError('올바른 휴대폰 번호를 입력해주세요.');
        return;
      }
    }

    // 통신사 선택 페이지로 이동
    navigate('/signup/carrier', {
      state: {
        snsType: state.snsType,
        snsId: state.snsId,
        email: state.email,
        name: state.name,
        phone: phoneNumber.replace(/[^\d]/g, '')
      }
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSkip = () => {
    navigate('/signup/gender', {
      state: {
        snsType: state?.snsType,
        snsId: state?.snsId,
        email: state?.email,
        name: state?.name,
      }
    });
  };

  const handleBack = async () => {
    // 회원가입 취소 시 Supabase 세션 삭제
    await supabase.auth.signOut();
    // 로그인 화면으로 이동
    navigate('/login', { replace: true });
  };

  // 브라우저 뒤로가기 버튼 처리
  useEffect(() => {
    const handlePopState = async () => {
      // 뒤로가기 시 Supabase 세션 삭제
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  return (
    <div className="phone-verification-container">
      <div className="phone-verification-header">
        <button className="phone-verification-back-btn" onClick={handleBack}>
          <img src={iconBack} alt="뒤로가기" />
        </button>
      </div>
      <div className="phone-verification-content">
        <h1 className="phone-verification-title">
          휴대폰 번호를<br />입력해 주세요.
        </h1>

        <div className="phone-verification-form">
          <div className="input-group">
            <label className="input-label">휴대폰 번호</label>
            {ENABLE_SMS_VERIFICATION ? (
              <div className="input-with-button">
                <input
                  type="text"
                  className={`phone-input ${phoneError ? 'error' : ''}`}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="010-0000-0000"
                  maxLength={13}
                  disabled={isCodeSent}
                />
                <button
                  className={`send-code-btn ${isCodeSent ? 'disabled' : ''}`}
                  onClick={handleSendCode}
                  disabled={isCodeSent}
                >
                  {isCodeSent ? '발송완료' : '인증번호 발송'}
                </button>
              </div>
            ) : (
              <input
                type="text"
                className={`phone-input ${phoneError ? 'error' : ''}`}
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="010-0000-0000"
                maxLength={13}
                style={{ width: '100%' }}
              />
            )}
            {phoneError && <p className="error-message">{phoneError}</p>}
          </div>

          {ENABLE_SMS_VERIFICATION && isCodeSent && (
            <div className="input-group">
              <label className="input-label">인증번호</label>
              <div className="input-with-button">
                <div className="verification-input-wrapper">
                  <input
                    type="text"
                    className={`verification-input ${codeError ? 'error' : ''}`}
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value.replace(/[^\d]/g, ''));
                      setCodeError('');
                    }}
                    placeholder="인증번호 6자리"
                    maxLength={6}
                    disabled={isVerified}
                  />
                  {timer > 0 && <span className="timer-inline">{formatTime(timer)}</span>}
                  {timer === 0 && <span className="timer-inline expired">00:00</span>}
                </div>
                <button
                  className={`verify-btn ${isVerified ? 'verified' : ''}`}
                  onClick={handleVerifyCode}
                  disabled={isVerified}
                >
                  {isVerified ? '인증완료' : '확인'}
                </button>
              </div>
              {codeError && <p className="error-message">{codeError}</p>}
              {timer === 0 && !isVerified && (
                <p className="resend-link" onClick={handleResendCode}>
                  인증번호 재발송
                </p>
              )}
            </div>
          )}

          <div className="sns-info">
            <p className="info-text">
              SNS 계정: {state?.snsType?.toUpperCase()} ({state?.email})
            </p>
          </div>
        </div>

        <button
          className={`complete-btn ${
            ENABLE_SMS_VERIFICATION
              ? (isVerified ? '' : 'disabled')
              : (phoneNumber.replace(/[^\d]/g, '').length === 11 ? '' : 'disabled')
          }`}
          onClick={handleComplete}
          disabled={
            ENABLE_SMS_VERIFICATION
              ? !isVerified
              : phoneNumber.replace(/[^\d]/g, '').length !== 11
          }
        >
          {ENABLE_SMS_VERIFICATION ? '인증번호 확인' : '다음'}
        </button>
        <button className="skip-btn" onClick={handleSkip}>
          건너뛰기
        </button>
      </div>
    </div>
  );
};

export default PhoneVerification;
